import usePagination from "@/hooks/useSyncedPagination";
import { useGetCustoms } from "@/api/customs.api";
import { useGetUsers } from "@/api/user.api";
import { useEffect, useMemo, useRef, useState } from "react";
import { DataTable } from "@/components/ui/customs/data-table/data-table";
import { FiltersPendingExpeditions } from "@/components/pendingShipments/filtersPendingExpeditions";
import {
  pendingExpeditionsTableColumns,
  pendingExpeditionsTableColumnsMobile,
} from "@/components/pendingShipments/pendingExpeditionsTableColumns";
import { useDeleteExpedition, useGetAllExpedition } from "@/api/expedition.api";
import { pendingCustomQuotationTableColumns } from "@/components/pendingShipments/pendingCustomQuotationTableColumns";
import clsx from "clsx";
import { DeleteExpeditionModal } from "@/components/pendingShipments/deleteExpeditionModal";
import { useNavigate } from "react-router-dom";
import { FinalizeModal } from "@/components/pendingShipments/finalize/finalizeModal";
import { SortingState } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { useExpeditionFilters } from "@/components/pendingShipments/useExpeditionFilters.reducer";
import { useDebounce } from "@/hooks/useDebounce";
import { ExpeditionMobileModal } from "@/components/pendingShipments/expeditionMobileModal";
import { ActionData } from "@/types/tableAction";
import { ExpeditionProcessStatusEnum } from "@/constants/enums/expeditionProcessStatus.enum";
import { toEndOfDayIso, toStartOfDayIso } from "@/lib/utils";

export const PendingShipmentsPage = () => {
  const [showCustomQuotation, setShowCustomQuotation] = useState<boolean>(true);
  const [showAlert, setShowAlert] = useState<boolean>(true);
  const [pagination, setPagination] = usePagination("enAttente");
  const [actionData, setActionData] = useState<ActionData | null>(null);
  const [sorting, setSorting] = useState<SortingState>([{ id: "createdAt", desc: true }]);
  const hasCheckedInitialQuotations = useRef(false);

  const { filters, actions } = useExpeditionFilters();
  const debouncedFilters = useDebounce(filters, 300);

  const navigate = useNavigate();

  const { data: customs } = useGetCustoms();
  const { data: users } = useGetUsers();

  const { mutateAsync: deleteExpedition, isPending: isDeletionPending } = useDeleteExpedition();

  const { data: expeditions } = useGetAllExpedition({
    processStatus: [
      ExpeditionProcessStatusEnum.PENDING_QUOTE,
      ExpeditionProcessStatusEnum.PENDING_CUSTOMER,
    ],
    search: debouncedFilters.search,
    country: debouncedFilters.country,
    createdAfter: debouncedFilters.dateRange?.[0]
      ? toStartOfDayIso(debouncedFilters.dateRange[0])
      : undefined,
    createdBefore: debouncedFilters.dateRange?.[1]
      ? toEndOfDayIso(debouncedFilters.dateRange[1])
      : undefined,
    createdById: debouncedFilters.userId,
    take: pagination.pageSize,
    skip: pagination.pageIndex * pagination.pageSize,
    sortBy: sorting[0]?.id,
    sortOrder: sorting[0]?.desc ? "DESC" : "ASC",
  });

  const expeditionsData = useMemo(() => {
    return expeditions?.data || [];
  }, [expeditions]);

  const { data: expeditionsCustomQuotationWithQuote } = useGetAllExpedition({
    processStatus: [ExpeditionProcessStatusEnum.PENDING_QUOTE_ACCEPTATION],
  });

  const hasCustomQuotationWithQuote = useMemo(() => {
    return (
      expeditionsCustomQuotationWithQuote?.data &&
      expeditionsCustomQuotationWithQuote?.data.length > 0
    );
  }, [expeditionsCustomQuotationWithQuote]);

  const expeditionsCustomQuotationWithQuoteData = useMemo(() => {
    return expeditionsCustomQuotationWithQuote?.data || [];
  }, [expeditionsCustomQuotationWithQuote]);

  useEffect(() => {
    if (
      !hasCheckedInitialQuotations.current &&
      expeditionsCustomQuotationWithQuoteData.length > 0
    ) {
      hasCheckedInitialQuotations.current = true;
      if (expeditionsCustomQuotationWithQuoteData.length > 3) {
        setShowCustomQuotation(false);
      }
    }
  }, [expeditionsCustomQuotationWithQuoteData]);

  const countCustomQuotationWithQuote: number = expeditionsCustomQuotationWithQuoteData.length;

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [
    debouncedFilters.search,
    debouncedFilters.country,
    debouncedFilters.userId,
    debouncedFilters.dateRange,
    setPagination,
  ]);

  return (
    <div className="flex h-fit w-full flex-1 flex-col">
      <div
        className={clsx(
          "bg-brand700 sticky top-0 z-20 flex w-full flex-row items-center justify-between gap-2 px-4 py-2 text-gray-100",
          (countCustomQuotationWithQuote === 0 || !showAlert) && "hidden",
        )}
      >
        <p>
          Vous avez{" "}
          <span className="font-bold">
            {countCustomQuotationWithQuote} demande
            {countCustomQuotationWithQuote > 1 ? "s" : ""} spécifique
            {countCustomQuotationWithQuote > 1 ? "s" : ""} en attente de finalisation
          </span>
        </p>
        <Button onClick={() => setShowAlert(!showAlert)} variant="ghost">
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex h-fit w-full flex-1 flex-col gap-8 p-4 md:p-8">
        <div className="flex flex-col items-start justify-between gap-2 md:flex-row md:items-center">
          <div className="flex flex-col gap-2">
            <h2 className="font-jakarta-sans text-3xl font-semibold">Expéditions en attente</h2>
          </div>
        </div>
        <FiltersPendingExpeditions
          filters={filters}
          actions={actions}
          customs={customs || []}
          users={users || []}
        />
        <div className="hidden w-full flex-col gap-10 md:flex">
          {hasCustomQuotationWithQuote && (
            <div className="relative flex flex-col gap-0">
              <button
                onClick={() => setShowCustomQuotation(!showCustomQuotation)}
                className="hover:bg-brand700/80 bg-brand700 absolute -top-2 left-3 z-10 flex w-fit flex-row items-center gap-2 rounded-2xl px-3 py-1 text-white"
              >
                <p className="text-sm font-medium">Demande spécifique</p>
                <div className="tetx-white rounded-full bg-red-800 px-2 py-0">
                  {countCustomQuotationWithQuote}
                </div>
              </button>
              {showCustomQuotation && (
                <DataTable
                  columns={pendingCustomQuotationTableColumns({
                    setActionData,
                    navigate,
                  })}
                  data={expeditionsCustomQuotationWithQuoteData}
                  hideHeader={true}
                />
              )}
            </div>
          )}
          <DataTable
            columns={pendingExpeditionsTableColumns({
              setActionData,
              navigate,
            })}
            data={expeditionsData}
            rowCount={expeditions?.count || 0}
            setPagination={setPagination}
            pagination={pagination}
            sorting={sorting}
            setSorting={setSorting}
            enableMultiSort={false}
          />
        </div>
        <div className="flex w-full flex-col gap-10 md:hidden">
          {hasCustomQuotationWithQuote && (
            <div className="relative flex flex-col gap-0">
              <button
                onClick={() => setShowCustomQuotation(!showCustomQuotation)}
                className="hover:bg-brand700/80 bg-brand700 absolute -top-2 left-3 z-10 flex w-fit flex-row items-center gap-2 rounded-2xl px-3 py-1 text-white"
              >
                <p className="text-sm font-medium">Demande spécifique</p>
                <div className="tetx-white rounded-full bg-red-800 px-2 py-0">
                  {countCustomQuotationWithQuote}
                </div>
              </button>
              {showCustomQuotation && (
                <DataTable
                  columns={pendingExpeditionsTableColumnsMobile({
                    setActionData,
                    navigate,
                  })}
                  data={expeditionsCustomQuotationWithQuoteData}
                  hideHeader={true}
                />
              )}
            </div>
          )}
          <DataTable
            columns={pendingExpeditionsTableColumnsMobile({
              setActionData,
              navigate,
            })}
            data={expeditionsData}
          />
        </div>
      </div>
      {actionData && actionData.action === "DELETE" && (
        <DeleteExpeditionModal
          expedition={actionData.expedition}
          open={actionData.action === "DELETE"}
          onClose={() => setActionData(null)}
          deleteExpedition={deleteExpedition}
          isDeletionPending={isDeletionPending}
        />
      )}
      {actionData && actionData.action === "FINALIZE" && (
        <FinalizeModal
          expedition={actionData.expedition}
          onClose={() => setActionData(null)}
          navigate={navigate}
        />
      )}
      {actionData && actionData.action === "MOBILE" && (
        <ExpeditionMobileModal
          expedition={actionData.expedition}
          onClose={() => setActionData(null)}
          navigate={navigate}
          setActionData={setActionData}
        />
      )}
    </div>
  );
};
